<template>
  <v-app>
    <v-container fluid class="py-8">
      <v-row justify="center">
        <v-col cols="12" lg="10" xl="8">
          <!-- Header com Avatar -->
          <v-card class="profile-hero mb-6 overflow-hidden" data-tour="up-avatar">
            <v-card-text class="text-center py-8">
              <v-avatar size="120" class="profile-hero-avatar mb-4">
                <v-img
                  :src="storeAvatarUrl || defaultAvatar"
                  :alt="storeAvatarUrl ? 'Imagem do Usuário' : 'Imagem Padrão'"
                />
              </v-avatar>

              <input
                type="file"
                ref="avatarInput"
                style="display: none"
                @change="uploadAvatar"
                accept="image/*"
              />

              <div>
                <h2 class="profile-hero-name mb-1">
                  {{ user.name || "Nome do Usuário" }}
                </h2>
                <p class="profile-hero-role mb-4">
                  <v-icon x-small color="primary" class="mr-1">mdi-shield-account-outline</v-icon>
                  {{ userType(user.role_id) }}
                </p>

                <v-btn
                  color="primary"
                  rounded
                  class="profile-avatar-btn"
                  @click="openAvatarInput"
                  :disabled="false"
                >
                  <v-icon left>mdi-camera</v-icon>
                  Alterar Foto
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <v-row>
            <!-- COLUNA ESQUERDA: PESSOAL + ENDEREÇO -->
            <v-col cols="12" md="6">
              <!-- Informações Pessoais -->
              <v-card class="profile-card mb-6" data-tour="up-info">
                <v-card-title class="d-flex align-center">
                  <v-icon left color="primary" small>mdi-account-circle</v-icon>
                  <span class="profile-card-title">Informações Pessoais</span>
                  <v-spacer />
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="toggleUserInfosEditing"
                        :color="userInfosEditing ? 'error' : 'primary'"
                      >
                        <v-icon>{{
                          userInfosEditing ? "mdi-close" : "mdi-pencil"
                        }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{
                      userInfosEditing
                        ? "Cancelar edição"
                        : "Editar informações pessoais"
                    }}</span>
                  </v-tooltip>
                </v-card-title>

                <v-divider />

                <v-card-text class="pt-6">
                  <v-form
                    ref="profileForm"
                    v-model="validUserForm"
                    lazy-validation
                  >
                    <v-text-field
                      v-model="user.name"
                      label="Nome completo"
                      prepend-inner-icon="mdi-account-outline"
                      outlined
                      color="primary"
                      :rules="userInfosEditing ? nameRules : []"
                      :disabled="!userInfosEditing"
                      class="mb-4"
                    />

                    <v-text-field
                      v-model="user.email"
                      label="Email"
                      prepend-inner-icon="mdi-email-outline"
                      outlined
                      color="primary"
                      type="email"
                      :rules="userInfosEditing ? emailRules : []"
                      :disabled="!userInfosEditing"
                      class="mb-4"
                    />

                    <v-text-field
                      v-model="user.cpf"
                      label="CPF"
                      prepend-inner-icon="mdi-card-account-details-outline"
                      outlined
                      color="primary"
                      v-mask="'###.###.###-##'"
                      :disabled="!userInfosEditing"
                      class="mb-4"
                    />

                    <v-row v-if="[1, 2].includes(user.role_id)">
                      <v-col cols="12" sm="4">
                        <v-select
                          v-model="user.professional_registration_type"
                          :items="profissionalRegisterTypes"
                          label="Tipo de Registro"
                          prepend-inner-icon="mdi-card-account-details"
                          outlined
                          color="primary"
                          :rules="
                            userInfosEditing ? requiredRulesUserWhenEditing : []
                          "
                          :disabled="!userInfosEditing"
                          class="mb-3"
                        />
                      </v-col>

                      <v-col cols="12" sm="4">
                        <v-select
                          v-model="user.professional_registration_state"
                          :items="brazilianStates"
                          label="UF do Conselho"
                          prepend-inner-icon="mdi-map"
                          outlined
                          color="primary"
                          :rules="
                            userInfosEditing ? requiredRulesUserWhenEditing : []
                          "
                          :disabled="!userInfosEditing"
                          class="mb-3"
                          hint="Conselhos são estaduais. Ex: CRM-SP"
                          persistent-hint
                        />
                      </v-col>

                      <v-col cols="12" sm="4">
                        <!-- Tipos com máscara específica (CRM, CRN, ...) -->
                        <v-text-field
                          v-if="user.professional_registration_type !== 'OUTRO'"
                          v-model="user.professional_registration"
                          :key="
                            'reg-' +
                            (user.professional_registration_type || 'none')
                          "
                          :label="registrationLabel"
                          prepend-inner-icon="mdi-medical-bag"
                          outlined
                          color="primary"
                          :rules="
                            userInfosEditing ? requiredRulesUserWhenEditing : []
                          "
                          :placeholder="registrationPlaceholder"
                          clearable
                          :disabled="!userInfosEditing"
                          v-mask="registrationMask"
                        />
                        <!-- "Outro": número livre, sem máscara -->
                        <v-text-field
                          v-else
                          v-model="user.professional_registration"
                          :key="'reg-outro'"
                          label="Número do Registro"
                          prepend-inner-icon="mdi-medical-bag"
                          outlined
                          color="primary"
                          :rules="
                            userInfosEditing ? requiredRulesUserWhenEditing : []
                          "
                          placeholder="Número do registro"
                          clearable
                          :disabled="!userInfosEditing"
                        />
                      </v-col>
                    </v-row>

                    <!-- "Outro": descrição do conselho -->
                    <v-row
                      v-if="
                        [1, 2].includes(user.role_id) &&
                        user.professional_registration_type === 'OUTRO'
                      "
                    >
                      <v-col cols="12">
                        <v-text-field
                          v-model="user.professional_registration_description"
                          label="Descrição do conselho"
                          prepend-inner-icon="mdi-card-text-outline"
                          outlined
                          color="primary"
                          :rules="
                            userInfosEditing ? requiredRulesUserWhenEditing : []
                          "
                          placeholder="Ex: CFO (Conselho Federal de Odontologia)"
                          hint="Informe o nome ou sigla do conselho"
                          persistent-hint
                          clearable
                          maxlength="100"
                          :disabled="!userInfosEditing"
                        />
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select
                          v-model="user.gender"
                          :items="genderOptions"
                          label="Gênero"
                          prepend-inner-icon="mdi-human-male-female"
                          outlined
                          color="primary"
                          :rules="
                            userInfosEditing ? requiredRulesUserWhenEditing : []
                          "
                          :disabled="!userInfosEditing"
                        />
                      </v-col>

                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="user.birth_date"
                          label="Data de Nascimento"
                          prepend-inner-icon="mdi-calendar"
                          type="date"
                          outlined
                          color="primary"
                          :rules="
                            userInfosEditing ? requiredRulesUserWhenEditing : []
                          "
                          :disabled="!userInfosEditing"
                        />
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card-text>

                <v-card-actions class="px-6 pb-6" v-if="userInfosEditing">
                  <v-btn
                    @click="toggleUserInfosEditing"
                    color="grey"
                    outlined
                    rounded
                  >
                    Cancelar
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    @click="saveUserInfos"
                    :loading="loading"
                    color="success"
                    :disabled="!validUserForm"
                    rounded
                  >
                    <v-icon left>mdi-check</v-icon>
                    Salvar
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- Endereço -->
              <v-card class="profile-card mb-6" data-tour="up-address">
                <v-card-title class="d-flex align-center">
                  <v-icon left color="primary" small>mdi-map-marker</v-icon>
                  <span class="profile-card-title">Endereço</span>
                  <v-spacer />
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="toggleAddressEditing"
                        :color="addressEditing ? 'error' : 'primary'"
                      >
                        <v-icon>{{
                          addressEditing ? "mdi-close" : "mdi-pencil"
                        }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{
                      addressEditing ? "Cancelar edição" : "Editar endereço"
                    }}</span>
                  </v-tooltip>
                </v-card-title>

                <v-divider />

                <v-card-text class="pt-6">
                  <v-form
                    ref="addressForm"
                    v-model="validAddressForm"
                    lazy-validation
                  >
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="user.address.cep"
                          label="CEP"
                          prepend-inner-icon="mdi-map-search-outline"
                          outlined
                          color="primary"
                          :rules="addressEditing ? cepRules : []"
                          maxlength="9"
                          @blur="onCepBlur"
                          v-mask="'#####-###'"
                          :disabled="!addressEditing"
                        />
                      </v-col>

                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="user.address.number"
                          label="Número"
                          prepend-inner-icon="mdi-numeric"
                          outlined
                          color="primary"
                          :rules="
                            addressEditing
                              ? requiredRulesAddressWhenEditing
                              : []
                          "
                          maxlength="6"
                          v-mask="'######'"
                          :disabled="!addressEditing"
                        />
                      </v-col>
                    </v-row>

                    <v-text-field
                      v-model="user.address.street"
                      label="Logradouro"
                      prepend-inner-icon="mdi-road-variant"
                      outlined
                      color="primary"
                      readonly
                      :disabled="!addressEditing"
                      class="mb-4"
                    />

                    <v-text-field
                      v-model="user.address.complement"
                      label="Complemento (opcional)"
                      prepend-inner-icon="mdi-home-plus-outline"
                      outlined
                      color="primary"
                      :disabled="!addressEditing"
                      placeholder="Ex: Apto 202, Bloco B"
                      class="mb-4"
                    />

                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="user.address.neighborhood"
                          label="Bairro"
                          prepend-inner-icon="mdi-home-city-outline"
                          outlined
                          color="primary"
                          readonly
                          :disabled="!addressEditing"
                        />
                      </v-col>

                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="user.address.city"
                          label="Cidade"
                          prepend-inner-icon="mdi-city"
                          outlined
                          color="primary"
                          readonly
                          :disabled="!addressEditing"
                        />
                      </v-col>
                    </v-row>

                    <v-text-field
                      v-model="user.address.state"
                      label="Estado"
                      prepend-inner-icon="mdi-map"
                      outlined
                      color="primary"
                      readonly
                      :disabled="!addressEditing"
                    />
                  </v-form>
                </v-card-text>

                <v-card-actions class="px-6 pb-6" v-if="addressEditing">
                  <v-btn
                    @click="toggleAddressEditing"
                    color="grey"
                    outlined
                    rounded
                  >
                    Cancelar
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    @click="saveAddress"
                    :loading="loading"
                    color="success"
                    :disabled="!validAddressForm"
                    rounded
                  >
                    <v-icon left>mdi-check</v-icon>
                    Salvar
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>

            <!-- COLUNA DIREITA: TELEFONES + (depois vem Segurança/Sistema no seu código) -->
            <v-col cols="12" md="6">
              <!-- Telefones -->
              <v-card class="profile-card mb-6" data-tour="up-phones">
                <v-card-title class="d-flex align-center">
                  <v-icon left color="primary" small>mdi-phone</v-icon>
                  <span class="profile-card-title">Telefones</span>
                  <v-spacer />

                  <v-tooltip bottom v-if="phonesEditing">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="addPhones"
                        :disabled="user.phones.length >= 3 || !phonesEditing"
                        color="primary"
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>{{
                      user.phones.length >= 3
                        ? "Limite de 3 telefones atingido"
                        : "Adicionar telefone"
                    }}</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="togglePhonesEditing"
                        :color="phonesEditing ? 'error' : 'primary'"
                      >
                        <v-icon>{{
                          phonesEditing ? "mdi-close" : "mdi-pencil"
                        }}</v-icon>
                      </v-btn>
                    </template>
                    <span>{{
                      phonesEditing ? "Cancelar edição" : "Editar telefones"
                    }}</span>
                  </v-tooltip>
                </v-card-title>

                <v-divider />

                <v-card-text class="pt-6">
                  <v-form
                    ref="phonesForm"
                    v-model="validPhonesForm"
                    lazy-validation
                  >
                    <div
                      v-for="(telefone, index) in user.phones"
                      :key="index"
                      class="mb-4 pa-4 rounded"
                      :class="telefone.is_primary ? 'primary--text' : ''"
                      style="border: 1px solid #e0e0e0"
                    >
                      <v-row align="center">
                        <v-col cols="12" sm="5">
                          <v-text-field
                            v-model="telefone.number"
                            :label="`Telefone ${index + 1}`"
                            prepend-inner-icon="mdi-phone"
                            outlined
                            dense
                            color="primary"
                            :rules="
                              phonesEditing
                                ? requiredRulesPhonesWhenEditing
                                : []
                            "
                            v-mask="'(##) #####-####'"
                            :disabled="!phonesEditing"
                          />
                        </v-col>

                        <v-col cols="12" sm="4">
                          <v-select
                            v-model="telefone.type"
                            :items="phoneTypes"
                            label="Tipo"
                            outlined
                            dense
                            color="primary"
                            :rules="
                              phonesEditing
                                ? requiredRulesPhonesWhenEditing
                                : []
                            "
                            :disabled="!phonesEditing"
                          />
                        </v-col>

                        <v-col cols="12" sm="2">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <div v-bind="attrs" v-on="on">
                                <v-checkbox
                                  v-model="telefone.is_primary"
                                  label="WhatsApp"
                                  dense
                                  color="primary"
                                  @change="defineMainPhone(index)"
                                  :disabled="!phonesEditing"
                                />
                              </div>
                            </template>
                            <span
                              >Marca este número como WhatsApp principal para
                              contato</span
                            >
                          </v-tooltip>
                        </v-col>

                        <v-col
                          cols="12"
                          sm="1"
                          v-if="user.phones.length > 1 && phonesEditing"
                        >
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                color="error"
                                v-bind="attrs"
                                v-on="on"
                                @click="removePhones(index)"
                                small
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Remover este telefone</span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </div>
                  </v-form>
                </v-card-text>

                <v-card-actions class="px-6 pb-6" v-if="phonesEditing">
                  <v-btn
                    @click="togglePhonesEditing"
                    color="grey"
                    outlined
                    rounded
                  >
                    Cancelar
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    @click="savePhones"
                    :loading="loading"
                    color="success"
                    :disabled="!validPhonesForm"
                    rounded
                  >
                    <v-icon left>mdi-check</v-icon>
                    Salvar
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- Segurança: alterar senha -->
              <v-card class="profile-card mb-6" data-tour="up-security">
                <v-card-title class="d-flex align-center">
                  <v-icon left color="primary" small>mdi-lock</v-icon>
                  <span class="profile-card-title">Segurança</span>
                </v-card-title>

                <v-divider />

                <v-card-text class="pt-6">
                  Mantenha sua conta segura atualizando sua senha
                  periodicamente.
                </v-card-text>

                <v-card-actions class="px-6 pb-6">
                  <v-spacer />
                  <v-btn color="primary" rounded @click="openPasswordModal">
                    <v-icon left>mdi-lock-reset</v-icon>
                    Alterar Senha
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Modal para Alterar Senha -->
      <v-dialog
        v-model="passwordModal"
        max-width="500px"
        persistent
        scrollable
        transition="dialog-bottom-transition"
      >
        <v-card class="password-dialog">
          <v-card-title class="password-dialog-title">
            <v-icon left color="white">mdi-lock-reset</v-icon>
            Alterar Senha
          </v-card-title>

          <v-card-text class="ds-dialog-body">
            <v-form ref="passwordForm" v-model="validPasswordForm">
              <section class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-lock</v-icon>
                    <span>Senha atual</span>
                  </div>
                </div>
                <v-text-field
                  v-model="currentPassword"
                  label="Senha Atual"
                  :type="currentPasswordVisible ? 'text' : 'password'"
                  :rules="currentPasswordRules"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                >
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          @click="togglePasswordVisibility('currentPassword')"
                        >
                          {{ currentPasswordVisible ? "mdi-eye" : "mdi-eye-off" }}
                        </v-icon>
                      </template>
                      <span>{{
                        currentPasswordVisible ? "Ocultar senha" : "Mostrar senha"
                      }}</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </section>

              <section class="ds-form-section">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-lock-reset</v-icon>
                    <span>Nova senha</span>
                  </div>
                </div>
                <v-text-field
                  v-model="newPassword"
                  label="Nova Senha"
                  :type="newPasswordVisible ? 'text' : 'password'"
                  :rules="passwordRules"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                >
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          @click="togglePasswordVisibility('newPassword')"
                        >
                          {{ newPasswordVisible ? "mdi-eye" : "mdi-eye-off" }}
                        </v-icon>
                      </template>
                      <span>{{
                        newPasswordVisible ? "Ocultar senha" : "Mostrar senha"
                      }}</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <!-- O que a senha precisa ter, marcado ao vivo (espelha o backend). -->
                <PasswordRequirements :senha="newPassword" class="mt-3" />

                <v-text-field
                  v-model="confirmPassword"
                  label="Confirmar Nova Senha"
                  :type="confirmPasswordVisible ? 'text' : 'password'"
                  :rules="confirmPasswordRules"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                >
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          @click="togglePasswordVisibility('confirmPassword')"
                        >
                          {{ confirmPasswordVisible ? "mdi-eye" : "mdi-eye-off" }}
                        </v-icon>
                      </template>
                      <span>{{
                        confirmPasswordVisible ? "Ocultar senha" : "Mostrar senha"
                      }}</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </section>
            </v-form>
          </v-card-text>

          <v-card-actions class="ds-dialog-actions">
            <v-btn outlined color="primary" @click="closePasswordModal">
              <v-icon left>mdi-close</v-icon>
              Cancelar
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              depressed
              @click="updatePassword"
              :disabled="!validPasswordForm"
              :loading="passwordLoading"
            >
              <v-icon left>mdi-check</v-icon>
              Alterar Senha
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
import PasswordRequirements from "@/components/shared/PasswordRequirements.vue";
import { regraDeSenhaForte } from "@/utils/politicaDeSenha";
import http from "@/services/http";
import { extractPasswordError } from "@/services/passwordService";
import { buscarEnderecoPorCep } from "@/utils/buscarEnderecoPorCep.js";
import { useSnackbar } from "@/composables/useSnackbar";
import tourMixin from "@/features/tour/tourMixin";
import { formatDateLong } from "@/utils/datetime";

export default {
  name: "UserProfile",
  components: { PasswordRequirements },

  mixins: [tourMixin],

  data() {
    return {
      userInfosEditing: false,
      addressEditing: false,
      phonesEditing: false,

      // ✅ agora é blob URL do endpoint /api/user/avatar
      imageUrl: null,
      defaultAvatar: require("@/assets/images/profile.jpeg"),

      validUserForm: false,
      validAddressForm: false,
      validPhonesForm: false,
      loading: false,
      loadingUserData: false,

      passwordModal: false,
      validPasswordForm: false,
      passwordLoading: false,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      currentPasswordVisible: false,
      newPasswordVisible: false,
      confirmPasswordVisible: false,

      user: {
        id: null,
        name: "",
        email: "",
        cpf: "",
        professional_registration: "",
        professional_registration_type: "",
        professional_registration_state: "",
        professional_registration_description: "",
        gender: "",
        birth_date: "",
        status: 1,
        clinic_id: null,
        role_id: null,
        created_at: null,
        updated_at: null,
        phones: [{ number: "", type: "celular", is_primary: true }],
        address: {
          cep: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city: "",
          state: "",
        },
        referral_source: "",
      },

      phoneTypes: [
        { text: "Celular", value: "celular" },
        { text: "Fixo", value: "fixo" },
      ],

      genderOptions: [
        { text: "Masculino", value: "Masculino" },
        { text: "Feminino", value: "Feminino" },
        { text: "Outro", value: "Outros" },
      ],

      profissionalRegisterTypes: [
        { text: "CRM (Medicina)", value: "CRM" },
        { text: "CRN (Nutrição)", value: "CRN" },
        { text: "CRF (Farmácia)", value: "CRF" },
        { text: "CREFITO (Fisioterapia)", value: "CREFITO" },
        { text: "CRP (Psicologia)", value: "CRP" },
        { text: "COREN (Enfermagem)", value: "COREN" },
        { text: "CRMV (Medicina Veterinária)", value: "CRMV" },
        { text: "Outro", value: "OUTRO" },
      ],

      // UFs brasileiras (conselhos são estaduais — ex: CRM-SP)
      brazilianStates: [
        "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG",
        "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR",
        "RS", "SC", "SE", "SP", "TO",
      ],
    };
  },

  computed: {
    tourId() {
      return "perfil-usuario";
    },
    requiredRulesUserWhenEditing() {
      return [(v) => !!v || "Campo obrigatório"];
    },
    requiredRulesAddressWhenEditing() {
      return [];
    },
    requiredRulesPhonesWhenEditing() {
      return [(v) => !!v || "Campo obrigatório"];
    },

    canEditAvatar() {
      return this.userInfosEditing || this.addressEditing || this.phonesEditing;
    },

    nameRules() {
      return [
        (v) => !!v || "Nome é obrigatório",
        (v) => (v && v.length >= 2) || "Nome deve ter pelo menos 2 caracteres",
      ];
    },

    emailRules() {
      return [
        (v) => !!v || "Email é obrigatório",
        (v) => /.+@.+\..+/.test(v) || "Email deve ser válido",
      ];
    },

    requiredRules() {
      return [(v) => !!v || "Campo obrigatório"];
    },

    cepRules() {
      return [
        (v) => !v || /^\d{5}-?\d{3}$/.test(v) || "CEP inválido",
      ];
    },

    currentPasswordRules() {
      return [(v) => !!v || "Informe sua senha atual"];
    },

    passwordRules() {
      return [
        (v) => !!v || "Senha é obrigatória",
        // A política completa (tamanho, maiúscula/minúscula, número e
        // símbolo) — a mesma do backend. Ver utils/politicaDeSenha.
        (v) => regraDeSenhaForte(v),
      ];
    },

    confirmPasswordRules() {
      return [
        (v) => !!v || "Confirme sua senha",
        (v) => v === this.newPassword || "As senhas não coincidem",
      ];
    },

    storeAvatarUrl() {
      return this.$store.getters["auth/profileImageUrl"];
    },

    registrationMask() {
      const t = (this.user.professional_registration_type || "").toUpperCase();
      // ✅ Task 5: Máscaras sem UF embutida — UF agora é campo separado
      if (t === "CRM") return "######";
      if (t === "CRP") return "##/######";
      if (t === "COREN") return "######";
      if (t === "CRN") return "######";
      if (t === "CRF") return "######";
      if (t === "CREFITO") return "######-A";
      if (t === "CRMV") return "######";
      if (t === "OUTRO") return ""; // sem máscara pra "outro"
      return "######";
    },

    registrationLabel() {
      const t = (this.user.professional_registration_type || "").toUpperCase();
      if (t === "OUTRO") return "Número do Registro"; // ✅ Task 4
      const map = {
        CRM: "Número do CRM",
        CRN: "Número do CRN",
        CRF: "Número do CRF",
        CREFITO: "Número do CREFITO",
        CRP: "Número do CRP",
        COREN: "Número do COREN",
        CRMV: "Número do CRMV",
      };
      return map[t] || "Número do Registro";
    },

    registrationPlaceholder() {
      const t = (this.user.professional_registration_type || "").toUpperCase();
      if (t === "CRM") return "Ex: 12345";
      if (t === "CRP") return "Ex: 06/123456";
      if (t === "COREN") return "Ex: 123456";
      if (t === "CRN") return "Ex: 123456";
      if (t === "CRF") return "Ex: 123456";
      if (t === "CREFITO") return "Ex: 123456-F";
      if (t === "CRMV") return "Ex: 12345";
      if (t === "OUTRO") return "Número do registro";
      return "Ex: 123456";
    },
  },

  watch: {
    "user.professional_registration_type"(newType, oldType) {
      if (!this.userInfosEditing) return;
      if (!oldType) return;
      if (newType !== oldType) {
        this.user.professional_registration = "";
        // Saiu de OUTRO ou entrou em OUTRO: limpa a descrição também.
        if (newType === "OUTRO" || oldType === "OUTRO") {
          this.user.professional_registration_description = "";
        }
      }
    },
  },

  async mounted() {
    await this.fetchUserData();
  },

  beforeDestroy() {
    // ✅ evita vazamento
    if (this.imageUrl?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(this.imageUrl);
      } catch {}
    }
  },

  methods: {
    formatDate: (v) => formatDateLong(v, { fallback: "N/A" }),
    // ================= TOUR GUIADO =================
    tourSteps() {
      return [
        {
          title: "Seu perfil 👤",
          description:
            "Aqui você mantém seus dados de cadastro e a segurança da conta. Vou apontar cada seção — reabra pelo “?” quando quiser.",
        },
        {
          anchor: "up-avatar",
          title: "Foto e identidade",
          description:
            "Sua foto, nome e função aparecem aqui. Use “Alterar Foto” para atualizar a imagem do seu perfil.",
        },
        {
          anchor: "up-info",
          title: "Informações pessoais",
          description:
            "Seus dados de cadastro. Clique no ícone de editar do card para alterar e salvar as informações.",
        },
        {
          anchor: "up-address",
          title: "Endereço",
          description:
            "Seu endereço, com busca automática pelo CEP para preencher rua, bairro e cidade.",
        },
        {
          anchor: "up-phones",
          title: "Telefones",
          description:
            "Cadastre um ou mais telefones de contato e marque qual é o principal.",
        },
        {
          anchor: "up-security",
          title: "Segurança",
          description:
            "Em “Alterar Senha” você troca a senha de acesso à sua conta quando precisar.",
        },
      ];
    },

    // Utilitários
    userType(roleId) {
      const roles = { 1: "Médico", 2: "Paciente", 3: "Assistente" };
      return roles[roleId] || "Usuário";
    },

    // --- Formatação de exibição (o backend remove a máscara ao salvar) ---
    onlyDigits(v) {
      return (v ?? "").toString().replace(/\D/g, "");
    },
    formatCpf(v) {
      const d = this.onlyDigits(v).slice(0, 11);
      if (d.length !== 11) return v ?? ""; // incompleto: mantém como está
      return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
    },
    formatCep(v) {
      const d = this.onlyDigits(v).slice(0, 8);
      if (d.length !== 8) return v ?? "";
      return `${d.slice(0, 5)}-${d.slice(5)}`;
    },
    formatPhoneBr(v) {
      const d = this.onlyDigits(v).slice(0, 11);
      if (d.length === 11)
        return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
      if (d.length === 10)
        return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return v ?? "";
    },

    toggleUserInfosEditing() {
      if (this.userInfosEditing) {
        this.fetchUserData();
        this.userInfosEditing = false;
        return;
      }
      this.userInfosEditing = true;
      this.$nextTick(() => this.$refs.profileForm?.validate());
    },
    toggleAddressEditing() {
      if (this.addressEditing) {
        this.fetchUserData();
        this.addressEditing = false;
        return;
      }
      this.addressEditing = true;
      this.$nextTick(() => this.$refs.addressForm?.validate());
    },
    togglePhonesEditing() {
      if (this.phonesEditing) {
        this.fetchUserData();
        this.phonesEditing = false;
        return;
      }
      this.phonesEditing = true;
      this.$nextTick(() => this.$refs.phonesForm?.validate());
    },

    // Modal de senha
    openPasswordModal() {
      this.passwordModal = true;
      this.currentPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
      this.currentPasswordVisible = false;
      this.newPasswordVisible = false;
      this.confirmPasswordVisible = false;
    },

    closePasswordModal() {
      this.passwordModal = false;
      this.currentPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
      this.currentPasswordVisible = false;
      this.newPasswordVisible = false;
      this.confirmPasswordVisible = false;
    },

    togglePasswordVisibility(field) {
      if (field === "currentPassword")
        this.currentPasswordVisible = !this.currentPasswordVisible;
      if (field === "newPassword")
        this.newPasswordVisible = !this.newPasswordVisible;
      if (field === "confirmPassword")
        this.confirmPasswordVisible = !this.confirmPasswordVisible;
    },

    // Telefones
    addPhones() {
      if (this.user.phones.length < 3) {
        this.user.phones.push({
          number: "",
          type: "celular",
          is_primary: false,
        });
      }
    },

    removePhones(index) {
      if (this.user.phones.length > 1) this.user.phones.splice(index, 1);
    },

    defineMainPhone(index) {
      this.user.phones.forEach((phone, i) => {
        if (i !== index) phone.is_primary = false;
      });
    },

    // Avatar upload
    openAvatarInput() {
      this.$refs.avatarInput.click();
    },

    async uploadAvatar(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      // validações
      if (!file.type.startsWith("image/")) {
        const { error } = useSnackbar();
        error("Por favor, selecione uma imagem válida.");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        const { error } = useSnackbar();
        error("A imagem deve ter no máximo 5MB.");
        return;
      }

      const { success, error } = useSnackbar();

      try {
        const formData = new FormData();
        formData.append("avatar", file);

        const response = await http.post("/api/user/upload-avatar", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data?.success) {
          success("Imagem atualizada com sucesso!");
          await this.$store.dispatch("auth/fetchProfileImage");
        } else {
          error("Erro ao atualizar a imagem.");
        }
      } catch (err) {
        error("Erro ao fazer upload da imagem.");
        console.error("Erro no upload:", err);
      } finally {
        // permite selecionar o mesmo arquivo novamente
        event.target.value = "";
      }
    },

    async onCepBlur() {
      const endereco = await buscarEnderecoPorCep(this.user.address.cep);
      if (!endereco) return;

      this.user.address.street = endereco.street;
      this.user.address.neighborhood = endereco.neighborhood;
      this.user.address.city = endereco.city;
      this.user.address.state = endereco.state;
      this.user.address.number = null;
    },

    async fetchUserData() {
      const { error } = useSnackbar();

      try {
        this.loadingUserData = true;

        const resp = await http.get("/api/user");
        const data = resp.data || {};

        // -------- address (safe default) --------
        let address = {
          cep: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city: "",
          state: "",
        };

        if (data.address && typeof data.address === "object") {
          address.cep = this.formatCep(data.address.cep ?? "");
          address.street = data.address.street ?? "";
          address.number = data.address.number ?? "";
          address.complement = data.address.complement ?? "";
          address.neighborhood = data.address.neighborhood ?? "";
          address.city = data.address.city ?? "";
          address.state = data.address.state ?? "";
        }

        // -------- phones (safe default) --------
        let phones = [{ number: "", type: "celular", is_primary: true }];

        if (Array.isArray(data.phones) && data.phones.length) {
          phones = data.phones.map((p) => ({
            id: p.id ?? undefined,
            number: this.formatPhoneBr(p.number ?? ""),
            type: p.type ?? "celular",
            is_primary: !!p.is_primary,
          }));
          if (!phones.some((p) => p.is_primary)) phones[0].is_primary = true;
        }

        // -------- professional registration (new + legacy fallback) --------
        const legacyType = data.crm ? "CRM" : data.crn ? "CRN" : "";
        const legacyValue = data.crm ?? data.crn ?? "";

        let professional_registration_type =
          data.professional_registration_type ?? legacyType ?? "";
        let professional_registration =
          data.professional_registration ?? legacyValue ?? "";

        // padroniza
        if (professional_registration_type) {
          professional_registration_type = String(
            professional_registration_type,
          )
            .toUpperCase()
            .trim();
        }
        if (professional_registration) {
          professional_registration = String(professional_registration)
            .toUpperCase()
            .trim();
        }

        // -------- atribui campo a campo (sem spread) --------
        this.user.id = data.id ?? null;
        this.user.name = data.name ?? "";
        this.user.email = data.email ?? "";
        this.user.cpf = this.formatCpf(data.cpf ?? "");
        this.user.gender = data.gender ?? "";
        this.user.birth_date = data.birth_date ?? "";
        this.user.status = data.status ?? 1;
        this.user.clinic_id = data.clinic_id ?? null;
        this.user.role_id = data.role_id ?? null;
        this.user.created_at = data.created_at ?? null;
        this.user.updated_at = data.updated_at ?? null;
        this.user.referral_source = data.referral_source ?? "";

        // endereço / telefones
        this.user.address = address;
        this.user.phones = phones;

        // registro profissional
        // Tipo conhecido? Senão, foi um "Outro" salvo com o nome do conselho
        // (ex: "CFO") — reconstruímos o estado OUTRO + descrição na volta.
        const knownTypes = [
          "CRM", "CRN", "CRF", "CREFITO", "CRP", "COREN", "CRMV",
        ];
        if (
          professional_registration_type &&
          !knownTypes.includes(professional_registration_type)
        ) {
          this.user.professional_registration_description =
            professional_registration_type;
          this.user.professional_registration_type = "OUTRO";
        } else {
          this.user.professional_registration_type =
            professional_registration_type;
          this.user.professional_registration_description = "";
        }
        this.user.professional_registration = professional_registration;
        this.user.professional_registration_state = (
          data.professional_registration_state ?? ""
        )
          .toString()
          .toUpperCase();

        // Se você tiver outros campos no objeto user (como professional_registration_type antigo etc),
        // coloque aqui explicitamente também.
      } catch (err) {
        error("Erro ao carregar os dados do perfil.");
        console.error("Erro ao buscar dados do usuário:", err);
      } finally {
        this.$nextTick(() => {
          this.loadingUserData = false;
        });
      }
    },

    async updatePassword() {
      const { success, error } = useSnackbar();

      if (!this.currentPassword) {
        error("Informe sua senha atual.");
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        error("As senhas não coincidem!");
        return;
      }

      try {
        this.passwordLoading = true;

        const response = await http.put("/api/user/edit-user-password", {
          current_password: this.currentPassword,
          password: this.newPassword,
        });

        if (response.data.status === "success") {
          success("Senha atualizada com sucesso!");
          this.closePasswordModal();
        } else {
          error("Erro ao alterar a senha.");
        }
      } catch (err) {
        // 422 = senha atual incorreta OU nova senha recusada pela política.
        // A explicação de verdade vem em `errors` ("Esta senha já apareceu em
        // vazamentos..."); o `message` é só um genérico "Erro de validação." —
        // e era ele que aparecia, sem dizer o que mudar na senha.
        const msg =
          err.response?.status === 422
            ? extractPasswordError(err, "A senha atual está incorreta.")
            : "Erro ao salvar a nova senha.";
        error(msg);
        console.error("Erro ao alterar senha:", err);
      } finally {
        this.passwordLoading = false;
      }
    },

    async saveUserInfos() {
      const { success, error } = useSnackbar();

      if (!this.$refs.profileForm.validate()) return;
      this.loading = true;

      try {
        const payload = {
          name: this.user.name,
          email: this.user.email,
          cpf: this.user.cpf,
          gender: this.user.gender,
          birth_date: this.user.birth_date,
        };

        if (![2, 3].includes(this.user.role_id)) {
          payload.professional_registration =
            this.user.professional_registration || null;

          // Se for "OUTRO", o tipo enviado ao backend é a descrição informada
          // (ex: "CFO" no lugar de "OUTRO"), mantendo um registro semântico real
          // — mesmo comportamento do cadastro (Register).
          if (this.user.professional_registration_type === "OUTRO") {
            const descricao = (
              this.user.professional_registration_description || ""
            ).trim();
            payload.professional_registration_type = descricao || "OUTRO";
          } else {
            payload.professional_registration_type =
              this.user.professional_registration_type || null;
          }

          payload.professional_registration_state =
            this.user.professional_registration_state || null;
        }

        await http.put("/api/user/edit-user", payload);

        success("Dados pessoais atualizados com sucesso!");
        this.userInfosEditing = false;
        await this.fetchUserData();
      } catch (err) {
        error("Erro ao atualizar os dados.");
        console.error("Erro ao salvar dados pessoais:", err);
      } finally {
        this.loading = false;
      }
    },

    async saveAddress() {
      const { success, error } = useSnackbar();

      this.loading = true;
      try {
        await http.put("/api/user/edit-address", {
          address: this.user.address,
        });
        success("Endereço atualizado com sucesso!");
        this.addressEditing = false;
        await this.fetchUserData();
      } catch (err) {
        error("Erro ao atualizar o endereço.");
        console.error("Erro ao salvar endereço:", err);
      } finally {
        this.loading = false;
      }
    },

    async savePhones() {
      const { success, error } = useSnackbar();

      const validPhones = this.user.phones
        .filter((phone) => phone.number.trim() !== "")
        .map((phone) => ({
          id: phone.id,
          number: phone.number,
          type: phone.type,
          is_primary: phone.is_primary,
        }));

      if (validPhones.length === 0) {
        error("Pelo menos um telefone deve ser informado.");
        return;
      }

      const hasPrimary = validPhones.some((phone) => phone.is_primary);
      if (!hasPrimary) validPhones[0].is_primary = true;

      this.loading = true;
      try {
        await http.put("/api/user/edit-phones", { phones: validPhones });
        success("Telefones atualizados com sucesso!");
        this.phonesEditing = false;
        await this.fetchUserData();
      } catch (err) {
        error("Erro ao atualizar os telefones.");
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ═══ Hero / avatar do topo ═══ */
.profile-hero {
  border-radius: 16px !important;
  background: linear-gradient(180deg, var(--ds-blue-50) 0%, var(--ds-surface-white) 60%) !important;
  box-shadow: 0 8px 24px rgba(31, 77, 63, 0.08),
    0 2px 6px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid var(--ds-blue-100);
}

.profile-hero-avatar {
  border: 4px solid var(--ds-white) !important;
  background: var(--ds-blue-100) !important;
  box-shadow: 0 6px 18px rgba(31, 77, 63, 0.25),
    0 0 0 2px var(--ds-primary) !important;
}

.profile-hero-name {
  font-size: 24px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.4px;
}

.profile-hero-role {
  font-size: 12px;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-avatar-btn {
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  height: 38px !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.22) !important;
}

/* ═══ Cards de informação ═══ */
.profile-card {
  border-radius: 12px !important;
  border: 1px solid var(--ds-blue-100) !important;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04) !important;
}

.profile-card .v-card__title {
  padding: 14px 20px !important;
  background: var(--ds-blue-50);
  min-height: 52px;
}

/* O card INTEIRO na cor do profile-hero — título, corpo e a área dos botões.
   Antes só título e corpo a recebiam: a faixa de baixo (`.v-card__actions`,
   onde ficam "Alterar Senha" e os "Salvar") ficava com o fundo padrão do card,
   uma cor diferente. `--ds-blue-50` é redefinida por tema (tokens.css), então
   salvia, clean e dark acertam juntos. `.v-card.profile-card` para vencer a
   regra global `.theme--dark.v-card` do salvia-dark.css. */
.v-card.profile-card,
.profile-card .v-card__text,
.profile-card .v-card__actions {
  background-color: var(--ds-blue-50) !important;
}

.profile-card-title {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: var(--ds-primary) !important;
  letter-spacing: 0.3px;
}

/* ═══ Dialog de senha ═══ */
.password-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.password-dialog-title {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-dark) 100%);
  color: var(--ds-white) !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  padding: 16px 24px !important;
}
</style>